import { c as _c } from "react/compiler-runtime";
import { type Configuration, DirectoryEntityType, type DirectoryEntityRecord, type ListMicrosoft365GroupMembersSort, type Snapshot, type SearchDirectoryEntitiesResponse, type Microsoft365User, type Microsoft365SharePointSite, type SaasEntity, type SaasEntitySnapshot, GetGwsBackupFilterApi, ListSaasEntitySnapshotsApi, GetSaasEntityIdByExternalIdApi, ListGoogleWorkspaceGroupsApi, ListGoogleWorkspaceOrgUnitsApi, ListInventoryItemSnapshotsApi, ListMicrosoft365UsersApi, ListMicrosoft365GroupsApi, ListMicrosoft365GroupMembersApi, GetMicrosoft365BackupScopeApi, type Microsoft365PolicyConfig, ListMicrosoft365SharePointSitesApi, ListSaasEntitiesApi, SaasEntityType, UpdateShouldFullBackupResourceApi, FileExplorerListPathApi, FileExplorerCountWorkspaceEntitiesApi, SearchDirectoryEntitiesApi, ListGroupConversationsApi } from '@repo/api-gw-sdk';
import { mutate } from 'swr';
import type { GWSharedDriveNode, GWUserNode } from '@/app/(dashboard)/saas-inventory/google-workspace/googleWorkspaceTypes';
import type { GWSnapshot } from '@/app/(dashboard)/saas-inventory/google-workspace/snapshotTypes';
import { dayjs } from '@/utils/dayjs';
import type { EntraIdGlobalSearchResponse } from './entraIdGlobalDirectorySearchTypes';
import type { HttpClient } from './httpClient';
const backupSettingsUrl = '/inventory/backup-settings';
const orgUnitsUrl = '/saas-inventory/google-workspace/org-units';
const groupsUrl = '/saas-inventory/google-workspace/groups';
const snapshotsBaseUrl = '/saas-inventory/google-workspace';
const entraIdBaseUrl = '/saas-inventory/entra-id';
const m365UsersUrl = '/saas-inventory/microsoft-365/users';
const m365GroupsUrl = '/saas-inventory/microsoft-365/groups';
const m365GroupMembersUrl = '/saas-inventory/microsoft-365/group-members';
const m365SharePointSitesUrl = '/saas-inventory/microsoft-365/sharepoint-sites';
const m365BackupScopeUrl = '/saas-inventory/microsoft-365/backup-scope';
const m365EntitiesUrl = '/saas-inventory/microsoft-365/entities';
const gwsEntitiesUrl = '/saas-inventory/google-workspace/entities';
const entitySnapshotsUrl = '/saas-inventory/entity-snapshots';
const entityIdByExternalIdUrl = '/saas-inventory/entity-id-by-external-id';

// terminated and hasSnapshot are set only in entities mode: the registry returns
// tombstones and snapshot presence, which the legacy directory endpoints do not.
export type Microsoft365UserListItem = Microsoft365User & {
  terminated?: boolean;
  hasSnapshot?: boolean;
};
export type Microsoft365SharePointSiteListItem = Microsoft365SharePointSite & {
  terminated?: boolean;
  hasSnapshot?: boolean;
};

// userId/siteId map to externalId (the Graph id), not entity.id: backup-scope coverage,
// threat lookups and the browse-index prefixes all key on the Graph id.
function mapSaasEntityToMicrosoft365User(entity: SaasEntity): Microsoft365UserListItem {
  const user = entity.properties?.microsoft365User;
  return {
    userId: entity.externalId,
    displayName: entity.displayName,
    userPrincipalName: user?.userPrincipalName,
    mail: user?.email,
    accountEnabled: user?.accountEnabled,
    hasMailbox: user?.hasMailbox,
    hasOnedrive: user?.hasOnedrive,
    mailboxType: user?.mailboxType,
    licenseStatus: user?.licenseStatus,
    licenses: user?.licenses,
    terminated: entity.terminated,
    hasSnapshot: entity.hasSnapshot
  };
}
function mapSaasEntityToSharePointSite(entity: SaasEntity): Microsoft365SharePointSiteListItem {
  return {
    siteId: entity.externalId,
    name: entity.displayName,
    webUrl: entity.properties?.sharePointSite?.webUrl,
    terminated: entity.terminated,
    hasSnapshot: entity.hasSnapshot
  };
}

// id mirrors the vault file-tree path so a row click drills into the same prefix the file listing
// uses; externalId is the Google directory id the browse index keys on.
function mapSaasEntityToGwsUser(entity: SaasEntity): GWUserNode {
  const email = entity.properties?.googleWorkspaceUser?.email ?? '';
  return {
    id: `users/${entity.externalId}`,
    name: entity.displayName || email || entity.externalId || '',
    type: 'user',
    email,
    lastBackup: '',
    terminated: entity.terminated,
    hasSnapshot: entity.hasSnapshot,
    saasEntityId: entity.id
  };
}
function mapSaasEntityToGwsSharedDrive(entity: SaasEntity): GWSharedDriveNode {
  return {
    id: `shared_drives/${entity.externalId}`,
    name: entity.displayName || entity.externalId || '',
    type: 'shared-drive',
    terminated: entity.terminated,
    hasSnapshot: entity.hasSnapshot,
    saasEntityId: entity.id
  };
}

// The registry returns tombstones (soft-deleted rows), so terminated rides along and the list marks
// them; DirectoryEntityRecord has no such field, hence the intersection.
function mapSaasEntityToGwsGroup(entity: SaasEntity, resourceId: string): GwsGroupEntityRecord {
  return {
    entityId: entity.externalId,
    entityType: DirectoryEntityType.DIRECTORY_ENTITY_TYPE_GROUP,
    name: entity.displayName,
    email: entity.properties?.group?.email ?? '',
    resourceId,
    terminated: entity.terminated,
    hasSnapshot: entity.hasSnapshot,
    saasEntityId: entity.id
  };
}
export type GwsGroupEntityRecord = DirectoryEntityRecord & {
  terminated?: boolean;
  hasSnapshot?: boolean;
  saasEntityId?: string;
};

// listSaasEntities decodes its pageToken as a base64 `<prefix>|pageIndex`. Encode it
// deterministically, not via encodePageToken, whose random uuid would change the SWR
// key every render and refetch in a loop.
function toEntitiesPageToken(pageToken?: string): string | undefined {
  return pageToken ? btoa(`page|${Number(pageToken)}`) : undefined;
}
const entityRequest = (type: SaasEntityType, search?: string) => ({
  filters: {
    type: {
      _in: [type]
    },
    ...(search ? {
      search
    } : {})
  }
});

// Matches the gateway's maxDirectoryListIds cap (directory_list_params.go) — a saved selection
// larger than one chunk is split across multiple requests instead of failing the whole resolve.
const DIRECTORY_IDS_CHUNK_SIZE = 500;
const chunkIds = (ids: string[]): string[][] => {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += DIRECTORY_IDS_CHUNK_SIZE) {
    chunks.push(ids.slice(i, i + DIRECTORY_IDS_CHUNK_SIZE));
  }
  return chunks;
};
interface EntityIdByExternalIdArgs {
  resourceId: string;
  entityType?: SaasEntityType;
  externalId?: string;
  enabled?: boolean;
}

// entityId is the Eon id listSaasEntities returns. Callers that only know the provider id + type
// (search deep links, the Microsoft 365 lists) pass those instead and the hook resolves them first.
interface EntitySnapshotsArgs extends EntityIdByExternalIdArgs {
  entityId?: string;
}
interface DirectorySearchArgs {
  resourceId: string;
  snapshotId?: string;
  query?: string;
  entityType?: DirectoryEntityType;
  entityId?: string;
  /** Entra ID only: drop enterprise applications the backed-up tenant does not own. */
  tenantOwnedOnly?: boolean;
  /**
   * Entra ID only: search every snapshot taken in this period, returning each
   * entity as the newest one saw it. Sent with snapshotId rather than instead
   * of it — a tenant whose reads still come from the index blob cannot answer
   * across snapshots and serves the named one.
   */
  fromTime?: Date;
  toTime?: Date;
  pageIndex: number;
  pageSize: number;
}
function snapshotToGWSnapshot(snapshot: Snapshot, isLatest: boolean): GWSnapshot {
  const date = (snapshot.pointInTime ?? snapshot.createdTime).toISOString();
  return {
    id: snapshot.id,
    date,
    label: `${dayjs(date).format('MMM D, YYYY')}${isLatest ? ' (Latest)' : ''}`,
    sizeBytes: snapshot.fullSizeBytes ?? 0,
    userCount: 0,
    sharedDriveCount: 0,
    userDailyBudgetExceededItemsCount: snapshot.userDailyBudgetExceededItemsCount
  };
}
function saasEntitySnapshotToGWSnapshot(snapshot: SaasEntitySnapshot, isLatest: boolean): GWSnapshot {
  const date = (snapshot.dateCreated ?? new Date()).toISOString();
  return {
    id: snapshot.snapshotId ?? '',
    date,
    label: `${dayjs(date).format('MMM D, YYYY')}${isLatest ? ' (Latest)' : ''}`,
    sizeBytes: 0,
    userCount: 0,
    sharedDriveCount: 0,
    hasPartialData: snapshot.hasPartialData
  };
}
export const saasInventory = (httpClient: HttpClient, config: Configuration, currentProjectId: string) => {
  const getBackupSettingsApi = new GetGwsBackupFilterApi(config);
  const updateBackupSettingsApi = new UpdateShouldFullBackupResourceApi(config);
  const listOrgUnitsApi = new ListGoogleWorkspaceOrgUnitsApi(config);
  const listGroupsApi = new ListGoogleWorkspaceGroupsApi(config);
  const listSnapshotsApi = new ListInventoryItemSnapshotsApi(config);
  const listM365UsersApi = new ListMicrosoft365UsersApi(config);
  const listM365GroupsApi = new ListMicrosoft365GroupsApi(config);
  const listM365GroupMembersApi = new ListMicrosoft365GroupMembersApi(config);
  const listM365SharePointSitesApi = new ListMicrosoft365SharePointSitesApi(config);
  const m365BackupScopeApi = new GetMicrosoft365BackupScopeApi(config);
  const listSaasEntitiesApi = new ListSaasEntitiesApi(config);
  const listSaasEntitySnapshotsApi = new ListSaasEntitySnapshotsApi(config);
  const getSaasEntityIdByExternalIdApi = new GetSaasEntityIdByExternalIdApi(config);
  const listPathApi = new FileExplorerListPathApi(config);
  const countWorkspaceEntitiesApi = new FileExplorerCountWorkspaceEntitiesApi(config);
  const searchDirectoryApi = new SearchDirectoryEntitiesApi(config);
  const listGroupConversationsApi = new ListGroupConversationsApi(config);

  // Every workspace kind lists snapshots through the same inventory endpoint and
  // differs only in its SWR cache namespace, so each passes its own baseUrl.
  const useSnapshotsRequest = (baseUrl: string, resourceId: string | null) => httpClient.execute(listSnapshotsApi.listInventoryItemSnapshots.bind(listSnapshotsApi), resourceId ? baseUrl : null, 'snapshots', currentProjectId, resourceId ?? '', 0, 100, 'eon');
  const useEntityIdByExternalIdRequest = ({
    resourceId,
    entityType,
    externalId,
    enabled = true
  }: EntityIdByExternalIdArgs) => {
    const result = httpClient.executeWithConfig(getSaasEntityIdByExternalIdApi.getSaasEntityIdByExternalId.bind(getSaasEntityIdByExternalIdApi), enabled && resourceId && externalId && entityType !== undefined ? entityIdByExternalIdUrl : null, `${resourceId}|${entityType}|${externalId}`, {
      keepPreviousData: false
    }, currentProjectId, resourceId,
    // Ignored when disabled: the SWR key above is null, so no request is made.
    {
      entityType: entityType ?? SaasEntityType.SAAS_ENTITY_TYPE_USER,
      externalId: externalId ?? ''
    });
    return {
      data: result.data?.entityId,
      isLoading: result.isLoading,
      error: result.error
    };
  };

  // Snapshots that captured one entity under a resource, newest first.
  const useEntitySnapshotsRequest = t0 => {
    const $ = _c(13);
    const {
      resourceId,
      entityType,
      externalId,
      entityId: knownEntityId,
      enabled: t1
    } = t0;
    const enabled = t1 === undefined ? true : t1;
    const t2 = enabled && !knownEntityId;
    let t3;
    if ($[0] !== entityType || $[1] !== externalId || $[2] !== resourceId || $[3] !== t2) {
      t3 = {
        resourceId,
        entityType,
        externalId,
        enabled: t2
      };
      $[0] = entityType;
      $[1] = externalId;
      $[2] = resourceId;
      $[3] = t2;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    const resolved = useEntityIdByExternalIdRequest(t3);
    const entityId = knownEntityId ?? resolved.data;
    let t4;
    if ($[5] !== enabled || $[6] !== entityId || $[7] !== resourceId) {
      t4 = httpClient.executeWithConfig(listSaasEntitySnapshotsApi.listSaasEntitySnapshots.bind(listSaasEntitySnapshotsApi), enabled && resourceId && entityId ? entitySnapshotsUrl : null, `${resourceId}|${entityId}`, {
        keepPreviousData: false
      }, currentProjectId, resourceId, 0, 3000, {
        entityId: entityId ?? ""
      });
      $[5] = enabled;
      $[6] = entityId;
      $[7] = resourceId;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    const result = t4;
    const t5 = result.data?.snapshots;
    const t6 = resolved.isLoading || result.isLoading;
    const t7 = resolved.error ?? result.error;
    let t8;
    if ($[9] !== t5 || $[10] !== t6 || $[11] !== t7) {
      t8 = {
        data: t5,
        isLoading: t6,
        error: t7
      };
      $[9] = t5;
      $[10] = t6;
      $[11] = t7;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    return t8;
  };

  // Reads the per-snapshot directory_entities index, so it reacts to the selected
  // snapshot and is empty for pre-index snapshots — unlike the live /groups/list
  // discovery behind useGroups. `emptyOnMissingIndex` turns the 404 a snapshot
  // without that index returns into an empty list instead of an error.
  const useDirectorySearchRequest = (baseUrl: string, {
    resourceId,
    snapshotId,
    query,
    entityType,
    entityId,
    tenantOwnedOnly,
    fromTime,
    toTime,
    pageIndex,
    pageSize
  }: DirectorySearchArgs, {
    emptyOnMissingIndex = false
  }: {
    emptyOnMissingIndex?: boolean;
  } = {}) => httpClient.execute((...args: Parameters<typeof searchDirectoryApi.searchDirectoryEntities>) => searchDirectoryApi.searchDirectoryEntities(...args).catch(error => {
    if (emptyOnMissingIndex && error.code === 404) {
      const noIndex: SearchDirectoryEntitiesResponse = {
        data: [],
        pagination: {
          recordsCount: 0
        }
      };
      return noIndex;
    }
    throw error;
  }), resourceId && snapshotId ? baseUrl : null, 'directorySearch', currentProjectId, resourceId, {
    snapshotId: snapshotId ?? '',
    query,
    entityType,
    entityId,
    tenantOwnedOnly,
    fromTime,
    toTime
  }, pageIndex, pageSize);
  return {
    useEntitySnapshots: useEntitySnapshotsRequest,
    googleWorkspace: {
      useSnapshots: (resourceId: string | null) => {
        const result = useSnapshotsRequest(snapshotsBaseUrl, resourceId);
        const snapshots: GWSnapshot[] = (result.data?.data ?? []).map((s: Snapshot, i: number) => snapshotToGWSnapshot(s, i === 0));
        return {
          data: snapshots,
          isLoading: result.isLoading
        };
      },
      useEntitySnapshots: (args: EntitySnapshotsArgs) => {
        const result = useEntitySnapshotsRequest(args);
        const snapshots: GWSnapshot[] = (result.data ?? []).map((s: SaasEntitySnapshot, i: number) => saasEntitySnapshotToGWSnapshot(s, i === 0));
        return {
          data: snapshots,
          isLoading: result.isLoading,
          error: result.error
        };
      },
      useListPath: (resourceId: string, prefix: string, pageSize: number, snapshotId?: string, pageToken?: string, queryExecutionId?: string, sortField?: string, sortDir?: string, includeTotalCount?: boolean, enabled = true) => httpClient.execute(listPathApi.fileExplorerListPath.bind(listPathApi), enabled && resourceId ? snapshotsBaseUrl : null, 'listPath', currentProjectId, resourceId, prefix, pageSize, pageToken, queryExecutionId, undefined, snapshotId, sortField, sortDir, includeTotalCount),
      // Gated on resourceId only: with no snapshotId, all-snapshots mode counts
      // across valid snapshots server-side.
      useEntityCount: (resourceId: string, prefix: string, snapshotId?: string, enabled = true) => httpClient.execute(countWorkspaceEntitiesApi.fileExplorerCountWorkspaceEntities.bind(countWorkspaceEntitiesApi), enabled && resourceId ? snapshotsBaseUrl : null, 'entityCount', currentProjectId, resourceId, prefix, snapshotId),
      // Snapshot-independent: the entities registry is the live directory, so no snapshotId.
      useEntityUsers: (resourceId: string, search?: string, pageSize?: number, pageToken?: string, enabled = true) => {
        const entities = httpClient.execute(listSaasEntitiesApi.listSaasEntities.bind(listSaasEntitiesApi), enabled && resourceId ? gwsEntitiesUrl : null, `${resourceId}|gws-users`, currentProjectId, resourceId, toEntitiesPageToken(pageToken), pageSize, entityRequest(SaasEntityType.SAAS_ENTITY_TYPE_USER, search));
        return {
          ...entities,
          data: entities.data ? {
            items: (entities.data.entities ?? []).map(mapSaasEntityToGwsUser),
            totalCount: entities.data.totalCount,
            nextPageToken: entities.data.nextToken
          } : undefined
        };
      },
      useEntitySharedDrives: (resourceId: string, search?: string, pageSize?: number, pageToken?: string, enabled = true) => {
        const entities = httpClient.execute(listSaasEntitiesApi.listSaasEntities.bind(listSaasEntitiesApi), enabled && resourceId ? gwsEntitiesUrl : null, `${resourceId}|gws-shared-drives`, currentProjectId, resourceId, toEntitiesPageToken(pageToken), pageSize, entityRequest(SaasEntityType.SAAS_ENTITY_TYPE_SHARED_DRIVE, search));
        return {
          ...entities,
          data: entities.data ? {
            items: (entities.data.entities ?? []).map(mapSaasEntityToGwsSharedDrive),
            totalCount: entities.data.totalCount,
            nextPageToken: entities.data.nextToken
          } : undefined
        };
      },
      useEntityGroups: (resourceId: string, search?: string, pageSize?: number, pageToken?: string, enabled = true) => {
        const entities = httpClient.execute(listSaasEntitiesApi.listSaasEntities.bind(listSaasEntitiesApi), enabled && resourceId ? gwsEntitiesUrl : null, `${resourceId}|gws-groups`, currentProjectId, resourceId, toEntitiesPageToken(pageToken), pageSize, entityRequest(SaasEntityType.SAAS_ENTITY_TYPE_GROUP, search));
        return {
          ...entities,
          data: entities.data ? {
            items: (entities.data.entities ?? []).map(entity => mapSaasEntityToGwsGroup(entity, resourceId)),
            totalCount: entities.data.totalCount,
            nextPageToken: entities.data.nextToken
          } : undefined
        };
      },
      useDirectorySearch: (args: DirectorySearchArgs) => useDirectorySearchRequest(snapshotsBaseUrl, args, {
        emptyOnMissingIndex: true
      }),
      // Lists the emails addressed to a group (distribution list), deduplicated to one row per
      // message across every member mailbox. Omitting snapshotId spans every snapshot.
      useGroupConversations: ({
        resourceId,
        groupEmail,
        snapshotId,
        pageIndex,
        pageSize,
        sortField,
        sortAsc
      }: {
        resourceId: string;
        groupEmail: string;
        snapshotId?: string;
        pageIndex: number;
        pageSize: number;
        sortField?: string;
        sortAsc?: boolean;
      }) => httpClient.execute(listGroupConversationsApi.listGroupConversations.bind(listGroupConversationsApi), resourceId && groupEmail ? snapshotsBaseUrl : null, 'groupConversations', currentProjectId, resourceId, {
        groupEmail,
        snapshotId,
        sortField,
        sortAsc
      }, pageIndex, pageSize),
      // Search and pagination run server-side; the pageToken is the page index in
      // decimal. Omitting pageSize keeps the full-listing behavior.
      useOrgUnits: (connectorId: string, search?: string, pageSize?: number, pageToken?: string, enabled = true) => httpClient.execute(listOrgUnitsApi.listGoogleWorkspaceOrgUnits.bind(listOrgUnitsApi), enabled ? orgUnitsUrl : null, connectorId, currentProjectId, connectorId, pageToken, pageSize, search ? {
        search
      } : undefined),
      // Resolves specific ids regardless of pagination. Part of the SWR key, so pass them sorted.
      // Chunked so a selection past the gateway's per-request id cap still resolves in full.
      useOrgUnitsByIds: (connectorId: string, ids: string[]) => httpClient.execute(async (projectId: string, cId: string, idsArg: string[]) => {
        const responses = await Promise.all(chunkIds(idsArg).map(idsChunk => listOrgUnitsApi.listGoogleWorkspaceOrgUnits(projectId, cId, undefined, undefined, {
          ids: idsChunk
        })));
        return {
          orgUnits: responses.flatMap(r => r.orgUnits ?? [])
        };
      }, ids.length > 0 ? orgUnitsUrl : null, connectorId, currentProjectId, connectorId, ids),
      useGroups: (connectorId: string, search?: string, pageSize?: number, pageToken?: string, enabled = true) => httpClient.execute(listGroupsApi.listGoogleWorkspaceGroups.bind(listGroupsApi), enabled ? groupsUrl : null, connectorId, currentProjectId, connectorId, pageToken, pageSize, search ? {
        search
      } : undefined),
      // Resolves specific ids regardless of pagination. Part of the SWR key, so pass them sorted.
      // Chunked so a selection past the gateway's per-request id cap still resolves in full.
      useGroupsByIds: (connectorId: string, ids: string[]) => httpClient.execute(async (projectId: string, cId: string, idsArg: string[]) => {
        const responses = await Promise.all(chunkIds(idsArg).map(idsChunk => listGroupsApi.listGoogleWorkspaceGroups(projectId, cId, undefined, undefined, {
          ids: idsChunk
        })));
        return {
          groups: responses.flatMap(r => r.groups ?? [])
        };
      }, ids.length > 0 ? groupsUrl : null, connectorId, currentProjectId, connectorId, ids),
      useBackupSettings: (resourceId: string) => httpClient.execute(getBackupSettingsApi.getGwsBackupFilter.bind(getBackupSettingsApi), backupSettingsUrl, resourceId, currentProjectId, resourceId),
      updateBackupSettings: (resourceId: string, gwsFilterJson: string) => updateBackupSettingsApi.updateShouldFullBackupResource(currentProjectId, resourceId, {
        gwsFilterJson
      }).then(() => mutate([backupSettingsUrl, resourceId, currentProjectId, resourceId]))
    },
    entraId: {
      useSnapshots: (resourceId: string | null) => useSnapshotsRequest(entraIdBaseUrl, resourceId),
      useDirectorySearch: (args: DirectorySearchArgs) => useDirectorySearchRequest(entraIdBaseUrl, args),
      // Cross-snapshot search over every Entra ID tenant: one record per directory
      // object id with the list of snapshots holding it. No generated SDK yet, so
      // the URL is hand-built; only the mock routes serve it today.
      useDirectoryGlobalSearch: ({
        query,
        entityType,
        pageIndex,
        pageSize
      }: {
        query: string;
        entityType?: DirectoryEntityType;
        pageIndex: number;
        pageSize: number;
      }) => {
        const params = new URLSearchParams({
          query,
          pageIndex: String(pageIndex),
          pageSize: String(pageSize)
        });
        if (entityType) {
          params.set('entityType', entityType);
        }
        const result = httpClient.get(query.trim() ? `/projects/${currentProjectId}/explorer/global/search/directory/entra-id?${params.toString()}` : null);
        return {
          data: result.body as EntraIdGlobalSearchResponse | undefined,
          isLoading: result.isLoading,
          error: result.error
        };
      },
      useDirectoryEdges: ({
        resourceId,
        snapshotId,
        entityId,
        entityType
      }: {
        resourceId: string;
        snapshotId?: string;
        entityId?: string;
        /** An id is only unique within a type; without it a collision can return another entity's edges. */
        entityType?: DirectoryEntityType;
      }) => httpClient.execute(searchDirectoryApi.listDirectoryEdges.bind(searchDirectoryApi), resourceId && snapshotId && entityId ? entraIdBaseUrl : null, 'directoryEdges', currentProjectId, resourceId, {
        snapshotId: snapshotId ?? '',
        entityId: entityId ?? '',
        entityType
      }),
      useDirectoryEntity: ({
        resourceId,
        snapshotId,
        entityId,
        entityType
      }: {
        resourceId: string;
        snapshotId?: string;
        entityId?: string;
        entityType?: DirectoryEntityType;
      }) => httpClient.execute(searchDirectoryApi.getDirectoryEntity.bind(searchDirectoryApi), resourceId && snapshotId && entityId && entityType ? entraIdBaseUrl : null, 'directoryEntity', currentProjectId, resourceId, {
        snapshotId: snapshotId ?? '',
        entityId: entityId ?? '',
        entityType: entityType ?? DirectoryEntityType.DIRECTORY_ENTITY_TYPE_UNSPECIFIED
      })
    },
    microsoft365: {
      // Search and pagination run server-side (the gateway filters in the DB and
      // returns nextPageToken); every distinct (search, page) tuple is its own
      // SWR key. Omitting pageSize keeps the full-listing behavior. enabled=false
      // skips the request entirely (null SWR key) so callers can mount the hook
      // unconditionally and only fetch when their UI needs the data.
      useUsers: (connectorId: string, search?: string, pageSize?: number, pageToken?: string, enabled = true, useEntities = false) => {
        const legacy = httpClient.execute(listM365UsersApi.listMicrosoft365Users.bind(listM365UsersApi), enabled && !useEntities ? m365UsersUrl : null, connectorId, currentProjectId, connectorId, pageToken, pageSize, search ? {
          search
        } : undefined);
        const entities = httpClient.execute(listSaasEntitiesApi.listSaasEntities.bind(listSaasEntitiesApi), enabled && useEntities ? m365EntitiesUrl : null, `${connectorId}|users`, currentProjectId, connectorId, toEntitiesPageToken(pageToken), pageSize, entityRequest(SaasEntityType.SAAS_ENTITY_TYPE_USER, search));
        if (!useEntities) {
          return legacy;
        }
        return {
          ...entities,
          data: entities.data ? {
            users: (entities.data.entities ?? []).map(mapSaasEntityToMicrosoft365User),
            totalCount: entities.data.totalCount,
            nextPageToken: entities.data.nextToken
          } : undefined
        };
      },
      useGroups: (connectorId: string, search?: string, pageSize?: number, pageToken?: string, enabled = true) => httpClient.execute(listM365GroupsApi.listMicrosoft365Groups.bind(listM365GroupsApi), enabled ? m365GroupsUrl : null, connectorId, currentProjectId, connectorId, pageToken, pageSize, search ? {
        search
      } : undefined),
      // Resolves specific ids regardless of pagination. Part of the SWR key, so pass them sorted.
      // Chunked so a selection past the gateway's per-request id cap still resolves in full.
      useGroupsByIds: (connectorId: string, ids: string[]) => httpClient.execute(async (projectId: string, cId: string, idsArg: string[]) => {
        const responses = await Promise.all(chunkIds(idsArg).map(idsChunk => listM365GroupsApi.listMicrosoft365Groups(projectId, cId, undefined, undefined, {
          ids: idsChunk
        })));
        return {
          groups: responses.flatMap(r => r.groups ?? [])
        };
      }, ids.length > 0 ? m365GroupsUrl : null, connectorId, currentProjectId, connectorId, ids),
      // Search, sort and pagination all run server-side, same as useUsers; the
      // pageToken is the page index in decimal. Omitting pageSize keeps the
      // full-listing behavior.
      useGroupMembers: (connectorId: string, groupId: string | null, search?: string, pageSize?: number, pageToken?: string, sort?: ListMicrosoft365GroupMembersSort) => httpClient.execute(listM365GroupMembersApi.listMicrosoft365GroupMembers.bind(listM365GroupMembersApi), connectorId && groupId ? m365GroupMembersUrl : null, groupId ?? '', currentProjectId, connectorId, groupId ?? '', pageToken, pageSize, search || sort ? {
        search,
        sort
      } : undefined),
      // Which users and sites a backup policy currently covers. Pass a draft
      // config to preview an unsaved selection instead of the tenant's policies.
      useBackupScope: (connectorId: string, config?: Microsoft365PolicyConfig, enabled = true) => httpClient.execute(m365BackupScopeApi.getMicrosoft365BackupScope.bind(m365BackupScopeApi), enabled && connectorId ? m365BackupScopeUrl : null, `${connectorId}|${config ? JSON.stringify(config) : 'saved'}`, currentProjectId, connectorId, config ? {
        config
      } : undefined),
      useSharePointSites: (connectorId: string, search?: string, pageSize?: number, pageToken?: string, enabled = true, useEntities = false) => {
        const legacy = httpClient.execute(listM365SharePointSitesApi.listMicrosoft365SharePointSites.bind(listM365SharePointSitesApi), enabled && !useEntities ? m365SharePointSitesUrl : null, connectorId, currentProjectId, connectorId, pageToken, pageSize, search ? {
          search
        } : undefined);
        const entities = httpClient.execute(listSaasEntitiesApi.listSaasEntities.bind(listSaasEntitiesApi), enabled && useEntities ? m365EntitiesUrl : null, `${connectorId}|sharepoint-sites`, currentProjectId, connectorId, toEntitiesPageToken(pageToken), pageSize, entityRequest(SaasEntityType.SAAS_ENTITY_TYPE_SHAREPOINT_SITE, search));
        if (!useEntities) {
          return legacy;
        }
        return {
          ...entities,
          data: entities.data ? {
            sharepointSites: (entities.data.entities ?? []).map(mapSaasEntityToSharePointSite),
            totalCount: entities.data.totalCount,
            nextPageToken: entities.data.nextToken
          } : undefined
        };
      },
      // Resolves specific ids regardless of pagination. Part of the SWR key, so pass them sorted.
      // Chunked so a selection past the gateway's per-request id cap still resolves in full.
      useSharePointSitesByIds: (connectorId: string, ids: string[]) => httpClient.execute(async (projectId: string, cId: string, idsArg: string[]) => {
        const responses = await Promise.all(chunkIds(idsArg).map(idsChunk => listM365SharePointSitesApi.listMicrosoft365SharePointSites(projectId, cId, undefined, undefined, {
          ids: idsChunk
        })));
        return {
          sharepointSites: responses.flatMap(r => r.sharepointSites ?? [])
        };
      }, ids.length > 0 ? m365SharePointSitesUrl : null, connectorId, currentProjectId, connectorId, ids)
    }
  };
};